import { useEffect, useRef } from 'react'
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react'

import type { EInvoice } from '@/lib/api/einvoice-api'
import type { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { usePublishQuickSaleEInvoice } from '@/hooks/use-einvoice-draft-mutations'
import { ApiError } from '@/lib/api'
import { formatApiError } from '@/lib/format-api-error'
import { m } from '@/lib/paraglide/messages'

type PublishWorkflow = ReturnType<typeof useWorkflowTracking>

// MISA đã nhận lệnh phát hành nhưng chưa có kết quả cuối (không trả lời, trả
// mã khác, báo trùng): gửi lại có thể thành hóa đơn thứ hai, nên không mời
// phát hành tay như lỗi chưa gửi được.
const SENT_UNSETTLED_CODES = new Set([
    'EINVOICE_PUBLICATION_AWAITING_MISA',
    'EINVOICE_PUBLICATION_PENDING',
    'EINVOICE_PUBLICATION_DUPLICATED',
])

/**
 * M-167: "Bán ngay" có tích "Xuất hóa đơn điện tử" — tích ô rồi bán là thu ngân
 * đã xác nhận, nên popup tự gửi MỘT lệnh tạo nháp + phát hành ngay khi mở. Chỉ
 * dựa vào dữ liệu có sẵn lúc mở (cờ, quyền, tick) để lệnh đi trước khi thu ngân
 * kịp bấm "In" đóng popup; server tự kiểm phần còn lại và không bao giờ phát
 * hành lần hai.
 */
export function useQuickSaleAutoPublish({
    orderId,
    requested,
    workflow,
}: {
    orderId: string
    requested: boolean
    workflow: PublishWorkflow
}) {
    const publish = usePublishQuickSaleEInvoice()
    const { mutateAsync } = publish
    const sent = useRef(false)
    useEffect(() => {
        if (!requested || sent.current) return
        sent.current = true
        workflow.setContext({
            orderStage: 'awaiting_stock_out',
            publishTrigger: 'auto',
        })
        void workflow
            .run(
                () => mutateAsync(orderId),
                (data) => data?.id,
            )
            .catch(() => {})
    }, [requested, orderId, mutateAsync, workflow])
    return {
        // Trước khi effect kịp gửi cũng tính là đang gửi, để popup không lóe
        // chuỗi "Tạo nháp" thủ công.
        pending: requested && (publish.isIdle || publish.isPending),
        succeeded: requested && publish.isSuccess,
        error: requested && publish.isError ? publish.error : null,
    }
}

export function QuickSaleAutoPublishNotice({
    pending,
    succeeded,
    error,
    noPermission,
    invoice,
}: {
    pending: boolean
    succeeded: boolean
    error: unknown
    noPermission: boolean
    invoice?: EInvoice | null
}) {
    if (pending)
        return (
            <p
                role="status"
                className="flex items-center gap-2 rounded-md border p-3 text-sm"
            >
                <Loader2 className="size-4 animate-spin text-primary" />
                {m.pos_einvoice_auto_publishing()}
            </p>
        )
    if (noPermission)
        return (
            <p className="rounded-md border p-3 text-sm text-muted-foreground">
                {m.pos_einvoice_auto_no_permission()}
            </p>
        )
    if (error) {
        const reason = formatApiError(error, m.einvoice_publish_failed())
        const sent =
            error instanceof ApiError && SENT_UNSETTLED_CODES.has(error.code)
        return (
            <div
                role="alert"
                className="space-y-1 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm"
            >
                <p className="flex items-center gap-2 font-medium text-destructive">
                    <AlertCircle className="size-4" />
                    {sent
                        ? m.pos_einvoice_auto_sent_unsettled_title()
                        : m.pos_einvoice_auto_failed_title()}
                </p>
                <p>{reason.title}</p>
                <p className="text-muted-foreground">
                    {sent
                        ? m.pos_einvoice_auto_sent_unsettled_hint()
                        : m.pos_einvoice_auto_failed_hint()}
                </p>
            </div>
        )
    }
    const status = invoice?.status
    if (!succeeded || !status) return null
    if (status === 'issued')
        return (
            <p
                role="status"
                className="flex items-center gap-2 text-sm font-medium text-green-700"
            >
                <CheckCircle className="size-4" />
                {m.pos_einvoice_auto_issued()}
            </p>
        )
    if (status === 'issuing' || status === 'publishing')
        return (
            <p role="status" className="text-sm font-medium">
                {m.pos_einvoice_auto_sent()}
            </p>
        )
    return null
}
